import {
  IApiEpBillingPaymentProvider,
  IApiEpBillingProductPurchaseRes,
} from "@smp/api";
import { FC, useLayoutEffect, useMemo } from "react";

import { useCloudpayments } from "@/hooks/use_cloudpayments";

import { isRecurringPayment } from "../payment_v2.static";

export type ICloudpaymentsProps = {
  invoice: IApiEpBillingProductPurchaseRes;
  provider: IApiEpBillingPaymentProvider;
  onDecline: () => void;
  onSuccess?: () => void;
};

export const Cloudpayments: FC<ICloudpaymentsProps> = ({
  invoice,
  provider,
  onDecline,
  onSuccess,
}) => {
  const { isLoaded, pay } = useCloudpayments();

  const payArguments = useMemo<
    | {
        method: Parameters<typeof pay>[0];
        options: Omit<Parameters<typeof pay>[1], "skin">;
      }
    | undefined
  >(() => {
    if (isRecurringPayment(invoice)) {
      return {
        method: "auth",
        options: {
          accountId: invoice.recurring_payment.confirm_widget_args.accountId,
          amount: invoice.recurring_payment.confirm_widget_args.amount,
          currency: invoice.recurring_payment.confirm_widget_args.currency,
          data: invoice.recurring_payment.confirm_widget_args.data,
          invoiceId: String(
            invoice.recurring_payment.confirm_widget_args.invoiceId,
          ),
          publicId: invoice.recurring_payment.confirm_widget_args.publicId,
          description:
            invoice.recurring_payment.confirm_widget_args.description,
        },
      };
    } else {
      const publicId =
        provider.currencies_supported[invoice.bill.currency]?.public_id;

      if (publicId) {
        return {
          method: "charge",
          options: {
            amount: Number(invoice.bill.amount),
            currency: invoice.bill.currency,
            data: {},
            invoiceId: String(invoice.bill.id),
            publicId,
            description: invoice.bill.description,
          },
        };
      }
    }
  }, [invoice, provider.currencies_supported]);

  useLayoutEffect(() => {
    if (!isLoaded || !payArguments) {
      return;
    }

    pay(
      payArguments.method,
      {
        ...payArguments.options,
        skin: "modern",
      },
      {
        onFail: () => {
          onDecline();
        },
        onSuccess,
      },
    );
  }, [
    invoice,
    invoice.bill,
    invoice.recurring_payment,
    isLoaded,
    onDecline,
    onSuccess,
    pay,
    payArguments,
  ]);

  return null;
};
